import { z } from 'zod'

import { PackageIdSchema } from './packages'

// Subscriptions and bank-transfer renewals. The super console is the source of truth (package, monthly price,
// paid-until date); it pushes them to each site (`PUT /api/control/subscription`). A site's lifecycle stage is computed
// from the paid-until date on every request, in Jakarta days, so nothing needs a cron to move a site from one stage to
// the next, and a confirmed payment restores everything at once.

/** `YYYY-MM-DD`, a calendar day. */
export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'A date such as 2026-10-31').refine((v) => isValidDate(v), 'Not a real date')

function isValidDate(v: string): boolean {
  const [y, m, d] = v.split('-').map(Number) as [number, number, number]
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m)
}

/** How many months a client can pay for at once. No discount: the total is the monthly price × months. */
export const SUBSCRIPTION_MONTHS = [1, 3, 6, 12] as const
export const SubscriptionMonthsSchema = z.union(SUBSCRIPTION_MONTHS.map((m) => z.literal(m)) as unknown as [z.ZodLiteral<1>, z.ZodLiteral<3>, z.ZodLiteral<6>, z.ZodLiteral<12>])
export type SubscriptionMonths = (typeof SUBSCRIPTION_MONTHS)[number]

/** Payment proof upload: JPG, PNG or PDF, at most 5 MB. */
export const PROOF_MAX_BYTES = 5 * 1024 * 1024
export const PROOF_TYPES = ['image/jpeg', 'image/png', 'application/pdf'] as const
export type ProofType = (typeof PROOF_TYPES)[number]

/** The account clients transfer to (a console setting, shown in the CMS renewal flow). */
export const BankAccountSchema = z.object({
  bank: z.string().trim().min(1).max(60),
  accountNumber: z
    .string()
    .trim()
    .regex(/^[0-9][0-9 -]{3,39}$/, 'Digits, spaces and dashes'),
  accountName: z.string().trim().min(1).max(100),
})
export type BankAccount = z.infer<typeof BankAccountSchema>

/** What the console pushes to a site (site document `subscription`). */
export const SiteSubscriptionSchema = z.object({
  /** No subscription needed (demo sites, Digimasi's own CMS): the lifecycle never applies. */
  exempt: z.boolean().default(false),
  /** Rupiah per month. */
  monthlyPrice: z.number().int().min(0).max(1_000_000_000),
  /** When the subscription started (for the record). */
  startedAt: IsoDateSchema.nullable().default(null),
  /** The last day that is paid for, inclusive. Null: not set up yet, the site runs as before. */
  paidUntil: IsoDateSchema.nullable(),
  /** The client's name on receipts (the console's site name). */
  clientName: z.string().trim().max(160).default(''),
  /** Where to transfer to. Null until the console has one. */
  bank: BankAccountSchema.nullable().default(null),
})
export type SiteSubscription = z.infer<typeof SiteSubscriptionSchema>

/**
 * Lifecycle stages, in order:
 * - `none`: no paid-until date set (or nothing pushed yet): everything works, nothing is shown
 * - `exempt`: no subscription needed
 * - `active`: paid, more than 7 days left
 * - `reminder`: the last 7 days before expiry (banner, and an email once sending is switched on)
 * - `grace`: expired up to 3 days ago: everything works, banner says expired
 * - `readonly`: the next 4 days: the CMS refuses changes except on the subscription screens
 * - `maintenance`: the next 7 days: the public site shows a maintenance page (503), CMS still read-only
 * - `off`: after that: the public site shows "not available" (503). Data is kept, nothing is deleted
 */
export const SUBSCRIPTION_STAGES = ['none', 'exempt', 'active', 'reminder', 'grace', 'readonly', 'maintenance', 'off'] as const
export const SubscriptionStageSchema = z.enum(SUBSCRIPTION_STAGES)
export type SubscriptionStage = z.infer<typeof SubscriptionStageSchema>

export const LIFECYCLE_DAYS = { reminder: 7, grace: 3, readonly: 4, maintenance: 7 } as const

/** The CMS refuses changes (except the subscription screens). */
export const isReadOnlyStage = (s: SubscriptionStage) => s === 'readonly' || s === 'maintenance' || s === 'off'
/** The public site is replaced by the maintenance or "not available" page. */
export const isPublicBlockedStage = (s: SubscriptionStage) => s === 'maintenance' || s === 'off'

const JAKARTA_OFFSET_MS = 7 * 3_600_000

/** Today in Jakarta (WIB, UTC+7, no daylight saving) as `YYYY-MM-DD`. */
export const jakartaDate = (nowMs: number = Date.now()) => new Date(nowMs + JAKARTA_OFFSET_MS).toISOString().slice(0, 10)

const toUtcMs = (date: string) => Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)))
const fromUtcMs = (ms: number) => new Date(ms).toISOString().slice(0, 10)

function daysInMonth(year: number, month: number) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export const addDays = (date: string, days: number) => fromUtcMs(toUtcMs(date) + days * 86_400_000)

/** `b − a` in whole days. */
export const daysBetween = (a: string, b: string) => Math.round((toUtcMs(b) - toUtcMs(a)) / 86_400_000)

/** The same day `months` later; the 31st becomes the last day of a shorter month. */
export function addMonths(date: string, months: number): string {
  const y = Number(date.slice(0, 4))
  const m = Number(date.slice(5, 7)) - 1 + months
  const year = y + Math.floor(m / 12)
  const month = (((m % 12) + 12) % 12) + 1
  const day = Math.min(Number(date.slice(8, 10)), daysInMonth(year, month))
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export const SubscriptionStateSchema = z.object({
  stage: SubscriptionStageSchema,
  /** Today in Jakarta, the day the stage was computed for. */
  today: z.string(),
  paidUntil: z.string().nullable(),
  /** Days from today to the last paid day: 0 on the last day, negative once expired. Null without a date. */
  daysLeft: z.number().int().nullable(),
  /** First day of each later stage, for the banners (null without a date). */
  readOnlyFrom: z.string().nullable(),
  maintenanceFrom: z.string().nullable(),
  offFrom: z.string().nullable(),
})
export type SubscriptionState = z.infer<typeof SubscriptionStateSchema>

/** Where a site is in its lifecycle on `today` (Jakarta). */
export function subscriptionState(sub: Pick<SiteSubscription, 'exempt' | 'paidUntil'> | null, today: string): SubscriptionState {
  const paidUntil = sub?.paidUntil ?? null
  const none = { today, paidUntil, daysLeft: null, readOnlyFrom: null, maintenanceFrom: null, offFrom: null }
  if (sub?.exempt) return { ...none, stage: 'exempt' }
  if (!paidUntil) return { ...none, stage: 'none' }
  const D = LIFECYCLE_DAYS
  const readOnlyFrom = addDays(paidUntil, D.grace + 1)
  const maintenanceFrom = addDays(readOnlyFrom, D.readonly)
  const offFrom = addDays(maintenanceFrom, D.maintenance)
  const daysLeft = daysBetween(today, paidUntil)
  const stage: SubscriptionStage =
    daysLeft >= D.reminder ? 'active' : daysLeft >= 0 ? 'reminder' : today < readOnlyFrom ? 'grace' : today < maintenanceFrom ? 'readonly' : today < offFrom ? 'maintenance' : 'off'
  return { stage, today, paidUntil, daysLeft, readOnlyFrom, maintenanceFrom, offFrom }
}

/**
 * The period an approved payment of `months` covers: from the later of today and the day after the current paid-until
 * date, for exactly `months` months. `to` is the new paid-until date.
 */
export function renewalPeriod(paidUntil: string | null, today: string, months: number): { from: string; to: string } {
  const next = paidUntil ? addDays(paidUntil, 1) : today
  const from = next > today ? next : today
  return { from, to: addDays(addMonths(from, months), -1) }
}

// ── Payments ───────────────────────────────────────────────────────────

export const PaymentStatusSchema = z.enum(['pending', 'approved', 'rejected'])
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>

/** A renewal payment as the site records it (the CMS history) and reports it to the console. */
export const SubscriptionPaymentSchema = z.object({
  id: z.string(),
  months: z.number().int(),
  monthlyPrice: z.number().int(),
  amount: z.number().int(),
  status: PaymentStatusSchema,
  /** Why it was rejected. */
  reason: z.string(),
  /** Where the proof is kept in the site's storage (R2), for the console's compliance log. */
  proofKey: z.string(),
  proofName: z.string(),
  proofType: z.string(),
  proofSize: z.number().int(),
  proofSha256: z.string(),
  submittedBy: z.string(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
  receiptNo: z.string().nullable(),
  periodFrom: z.string().nullable(),
  periodTo: z.string().nullable(),
})
export type SubscriptionPayment = z.infer<typeof SubscriptionPaymentSchema>

/** GET /api/admin/subscription/status: what every CMS user's banner needs. */
export const SubscriptionStatusSchema = SubscriptionStateSchema.extend({ package: PackageIdSchema })
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>

/** GET /api/admin/subscription (Admins): the subscription box, the renewal flow and the payment history. */
export const AdminSubscriptionSchema = z.object({
  package: PackageIdSchema,
  state: SubscriptionStateSchema,
  exempt: z.boolean(),
  monthlyPrice: z.number().int().nullable(),
  startedAt: z.string().nullable(),
  bank: BankAccountSchema.nullable(),
  /** A payment waits for confirmation: no new one until it is decided. */
  pending: z.boolean(),
  payments: z.array(SubscriptionPaymentSchema),
})
export type AdminSubscription = z.infer<typeof AdminSubscriptionSchema>

// ── Control API (console ↔ site) ───────────────────────────────────────

export const ControlSubscriptionSchema = z.object({ stored: SiteSubscriptionSchema.nullable(), state: SubscriptionStateSchema })
export type ControlSubscription = z.infer<typeof ControlSubscriptionSchema>

export const ControlPaymentListSchema = z.object({ items: z.array(SubscriptionPaymentSchema) })

/** The console's decision on a payment, sent to the site (repeatable: the same decision twice changes nothing). */
export const ControlPaymentDecisionSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('approved'), decidedAt: z.string().max(40), receiptNo: z.string().trim().min(1).max(40), periodFrom: IsoDateSchema, periodTo: IsoDateSchema }),
  z.object({ status: z.literal('rejected'), decidedAt: z.string().max(40), reason: z.string().trim().min(1).max(500) }),
])
export type ControlPaymentDecision = z.infer<typeof ControlPaymentDecisionSchema>

/** What the public web Worker asks bak-api (`GET /api/public/site-status`) to decide whether to show the site. */
export const PublicSiteStatusSchema = z.object({
  stage: SubscriptionStageSchema,
  siteName: z.string(),
  locales: z.array(z.string()),
  defaultLocale: z.string(),
})
export type PublicSiteStatus = z.infer<typeof PublicSiteStatusSchema>

/** Rupiah without decimals: `Rp 1.500.000`. */
export const formatRupiah = (amount: number) => `Rp ${String(Math.round(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
